
import React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { CustomVariable, CreateCustomVariablePayload } from '@/types/api';
import { useCustomVariableStore } from '@/store/customVariableStore';

const formSchema = z.object({
  key: z.string().min(1, 'Key is required'),
  value: z.string(),
  enabled: z.boolean(),
  is_freq_used: z.boolean(),
  order_number: z.number().optional(),
});

type VariableFormValues = z.infer<typeof formSchema>;

interface VariableFormProps {
  isOpen: boolean;
  onClose: () => void;
  variable: CustomVariable | null;
}

export function VariableForm({
  isOpen,
  onClose,
  variable,
}: VariableFormProps) {
  const { createVariable, updateVariable } = useCustomVariableStore();

  const form = useForm<VariableFormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      key: variable?.key ?? '',
      value: variable?.value ?? '',
      enabled: variable?.enabled ?? true,
      is_freq_used: variable?.is_freq_used ?? false,
      order_number: variable?.order_number ?? 0,
    },
  });

  // Reset form when the variable prop changes
  React.useEffect(() => {
    form.reset({
      key: variable?.key ?? '',
      value: variable?.value ?? '',
      enabled: variable?.enabled ?? true,
      is_freq_used: variable?.is_freq_used ?? false,
      order_number: variable?.order_number ?? 0,
    });
  }, [variable, form]);

  const onSubmit = async (values: VariableFormValues) => {
    const payload = {
      ...values,
      order_number: values.order_number ?? 0,
    };

    if (variable) {
      await updateVariable(variable.id, payload);
    } else {
      await createVariable(payload as CreateCustomVariablePayload);
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {variable ? 'Edit Variable' : 'Create Variable'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="key"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Key</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., PLAN_MODE" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="value"
                render={({ field }) => (
                  <FormItem className="col-span-2">
                    <FormLabel>Value</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., true" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex flex-col gap-4">
              <FormField
                control={form.control}
                name="is_freq_used"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                    <div className="space-y-0.5">
                      <FormLabel>Simple Mode</FormLabel>
                      <FormDescription>
                        Show this variable in the simplified view.
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="enabled"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 shadow-sm">
                    <div className="space-y-0.5">
                      <FormLabel>Enabled</FormLabel>
                      <FormDescription>
                        Is this variable active globally?
                      </FormDescription>
                    </div>
                    <FormControl>
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                {variable ? 'Save Changes' : 'Create'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}